import { cn } from "@ui/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { useTranslations } from "next-intl";
import { DottedBorder } from "./GradientBorder";
import clsx from "clsx";
import { getImageProps } from "@utilities/payload";
import type { CustomTableBlock, Media } from "@mw/shared/payload-types";
import Image from "next/image";

// Derive from payload types to stay in sync with CMS
type ColumnConfig = NonNullable<CustomTableBlock["columnConfig"]>[number];
export type ColumnColor = NonNullable<ColumnConfig["textColor"]>;

const COLUMN_COLOR_CLASSES: Record<ColumnColor, string> = {
  default: "",
  gray: "tw-text-gray-500",
};

export interface TableSimpleColumn {
  key: string;
  label?: string;
  color?: ColumnColor;
  headerIcon?: Media;
}

export interface TableSimpleCell {
  value: React.ReactNode | string;
  icon?: React.ReactNode;
  color?: ColumnColor;
}

const tableSimpleHeaderVariants = cva("tw-flex tw-flex-col tw-gap-2 tw-text-left", {
  variants: {
    size: {
      default: "tw-type-sm md:tw-type-base tw-py-8",
      xs: "tw-type-xs tw-py-4",
    },
    spacing: {
      default: "tw-px-2.5",
      "flush-start": "tw-pr-2.5",
    },
  },
  defaultVariants: {
    size: "default",
    spacing: "default",
  },
});

const tableSimpleCellVariants = cva("tw-relative tw-break-words tw-whitespace-pre-wrap", {
  variants: {
    size: {
      default: "tw-type-sm md:tw-type-base tw-py-8",
      xs: "tw-type-xs tw-py-4",
    },
    spacing: {
      default: "tw-px-2.5",
      "flush-start": "tw-pr-2.5 tw-text-left",
    },
  },
  defaultVariants: {
    size: "default",
    spacing: "default",
  },
});

type TableSimpleCellVariantProps = VariantProps<typeof tableSimpleCellVariants>;

export type TableSimpleSize = NonNullable<TableSimpleCellVariantProps["size"]>;
export type TableSimpleCellSpacing = NonNullable<TableSimpleCellVariantProps["spacing"]>;

export type TableSimpleVariant = "table" | "dl";

export interface TableSimpleProps {
  columns: TableSimpleColumn[];
  rows: Record<string, TableSimpleCell | null | undefined>[];
  /** Render as a description list (`dl/dt/dd`) instead of a table. Requires exactly 2 columns: the first becomes `dt`, the second `dd`. */
  variant?: TableSimpleVariant;
  withHeaderHighlighted?: boolean;
  endingBorder?: boolean;
  size?: TableSimpleCellVariantProps["size"];
  cellSpacing?: TableSimpleCellVariantProps["spacing"];
  className?: string;
}

const VARIANT_ROOT_ELEMENT: Record<TableSimpleVariant, "div" | "dl"> = {
  table: "div",
  dl: "dl",
};

/** Returns the element tag for a cell: `div` for table variant, `dt`/`dd` for dl variant. */
function getCellTag(variant: TableSimpleVariant, colIndex: number) {
  if (variant !== "dl") return "div";
  return colIndex === 0 ? "dt" : "dd";
}

export function TableSimple({
  columns,
  rows,
  variant = "table",
  withHeaderHighlighted = true,
  endingBorder = true,
  size = "default",
  cellSpacing = "default",
  className,
}: TableSimpleProps) {
  const t = useTranslations("ClientKeys.Components.TableSimple");
  const isDl = variant === "dl";
  const hasHeaderRow = columns.some(column => !!column.label?.trim().length || !!column.headerIcon);

  if (!columns.length || !rows.length) {
    return null;
  }

  const gridTemplateColumns = Array(columns.length).fill("minmax(140px, 1fr)").join(" ");
  const Root = VARIANT_ROOT_ELEMENT[variant];

  return (
    <div className={cn("tw-w-full tw-overflow-x-auto", className)}>
      <Root
        aria-label={isDl ? undefined : t("aria_label")}
        className='tw-grid tw-min-w-fit'
        style={{ gridTemplateColumns }}
        {...(!isDl && { role: "table" })}
      >
        {/* Header row */}
        {hasHeaderRow && (
          <div className='contents' {...(!isDl && { role: "rowgroup" })}>
            {columns.map((column, colIndex) => {
              const iconProps = getImageProps(column.headerIcon, { placeholder: true });
              return (
                <div
                  key={`header-${colIndex}`}
                  className={clsx(
                    tableSimpleHeaderVariants({ size, spacing: cellSpacing }),
                    withHeaderHighlighted && "tw-opacity-60",
                  )}
                  {...(!isDl && { role: "columnheader" })}
                >
                  {iconProps && <Image {...iconProps} className='tw-h-5 tw-max-w-48' alt='' />}
                  {column.label && column.label}
                </div>
              );
            })}
          </div>
        )}

        {/* Data rows */}
        {rows.map((row, rowIndex) => (
          <div key={`row-${rowIndex}`} className='tw-relative tw-contents' {...(!isDl && { role: "row" })}>
            {columns.map((column, colIndex) => {
              const cell = row[column.key];
              const hasIcon = cell?.icon != null;
              const shouldRenderTopBorder = rowIndex > 0 || hasHeaderRow;
              const cellColor = cell?.color ?? column.color ?? "default";
              const CellTag = getCellTag(variant, colIndex);

              return (
                <CellTag
                  key={`cell-${rowIndex}-${colIndex}`}
                  className={clsx(
                    tableSimpleCellVariants({ size, spacing: cellSpacing }),
                    COLUMN_COLOR_CLASSES[cellColor],
                    hasIcon && "tw-pl-0",
                  )}
                  {...(!isDl && { role: "cell" })}
                >
                  {shouldRenderTopBorder && <DottedBorder side='top' />}
                  {rowIndex === rows.length - 1 && endingBorder && <DottedBorder side='bottom' />}
                  {hasIcon ? (
                    <div className='tw-flex tw-items-start tw-gap-4'>
                      <div className='tw-shrink-0'>{cell.icon}</div>
                      <div className='tw-flex-1'>{cell?.value ?? ""}</div>
                    </div>
                  ) : (
                    (cell?.value ?? "")
                  )}
                </CellTag>
              );
            })}
          </div>
        ))}
      </Root>
    </div>
  );
}

TableSimple.displayName = "TableSimple";
